import { CAMP_GA4_MAP } from '../config/campAnalytics';

const loadedIds = new Set();

/**
 * Initializes GA4 for a specific camp and fires a page_view event with custom info.
 *
 * Uses send_page_view: false on the config call so that custom params are sent
 * only with the explicit page_view event and do NOT persist to future events.
 *
 * Call this when a user OPENS a page — no user action required.
 */
export function initCampGA4(campCode, info = {}) {
  const measurementId = CAMP_GA4_MAP[campCode];
  if (!measurementId) return;

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
  }

  if (!loadedIds.has(measurementId)) {
    loadedIds.add(measurementId);
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }

  // Initialize the property without firing a page_view yet, and without setting
  // any persistent config params (which would bleed into all subsequent events).
  window.gtag('config', measurementId, { send_page_view: false });

  // Fire page_view as an explicit event so the custom params are scoped to this
  // event only and do NOT carry over to product_view, store_camp_filter, etc.
  window.gtag('event', 'page_view', {
    send_to: measurementId,
    ...info,
  });
}

/**
 * Fires a named custom event to the camp's GA4 property.
 *
 * Call this for USER ACTIONS that happen after the page loads —
 * add to cart, begin checkout, booking confirmation, payment, etc.
 *
 * send_to ensures the event only goes to this camp's GA4 property
 * and not to any other property that may be active on the page.
 *
 * Examples:
 *   trackGA4Event(campCode, 'add_to_cart', { program_id: '...', price: 299, quantity: 2 })
 *   trackGA4Event(campCode, 'begin_checkout', { program_name: '...', campers: 2 })
 *   trackGA4Event(campCode, 'purchase', { transaction_id: '...', value: 598, currency: 'USD' })
 */
export function trackGA4Event(campCode, eventName, eventData = {}) {
  const measurementId = CAMP_GA4_MAP[campCode];
  if (!measurementId || !window.gtag) return;

  window.gtag('event', eventName, {
    send_to: measurementId,
    ...eventData,
  });
}
